import React, { useState, useEffect, useRef } from 'react';
import { GlassCard } from '../_shared/GlassCard';
import { Footer } from '../_shared/Footer';
import {
  Eye, Users, TrendingUp, Play, Award, Activity,
  Youtube, Instagram, Facebook, Linkedin,
  MessageCircle, Heart, Repeat2,
} from 'lucide-react';

// ——— Animated Counter Hook ———
const useCounter = (end, duration = 1400) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAnimated.current) {
        hasAnimated.current = true;
        const start = performance.now();
        const animate = (now) => {
          const progress = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(end * eased));
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);
  return [count, ref];
};

// ——— Stat Card ———
const StatCard = ({ icon: Icon, iconColor, label, value, suffix = '', subtext, displayValue }) => {
  const [animatedValue, ref] = useCounter(value);
  return (
    <GlassCard className="flex flex-col justify-between min-h-[180px]">
      <div ref={ref}>
        <div className={`${iconColor} p-3 rounded-xl mb-4 inline-flex`}>
          <Icon size={22} />
        </div>
        <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-2">{label}</p>
        <p className="text-4xl font-black text-white tracking-tight">
          {displayValue || <>{animatedValue.toLocaleString()}{suffix}</>}
        </p>
        {subtext && <p className="text-white/50 text-sm mt-3">{subtext}</p>}
      </div>
    </GlassCard>
  );
};

// ——— Reel Card Grid ———
const ReelGrid = ({ reels, metricLabel = 'Views' }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
    {reels.map((reel, i) => (
      <a
        key={i}
        href={reel.url}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative block rounded-[2rem] overflow-hidden transition-transform duration-300 hover:scale-[1.03]"
        style={{ aspectRatio: '9/16' }}
      >
        <div className={`absolute inset-0 bg-gradient-to-br ${reel.gradient}`} />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-5xl mb-3 opacity-30 group-hover:opacity-0 transition-opacity duration-300">{reel.emoji}</span>
        </div>
        <div className="absolute top-1/4 left-3 right-3">
          <div className="bg-black/60 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/10">
            <p className="text-white text-xs sm:text-sm font-bold leading-tight text-center">{reel.title}</p>
          </div>
        </div>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 backdrop-blur-0 group-hover:backdrop-blur-sm transition-all duration-300 flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
            <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center">
              <Play size={24} className="text-white ml-1" fill="white" />
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
          <p className="text-white/70 text-xs font-mono">{reel.metric} {metricLabel}</p>
        </div>
        <div className="absolute top-3 left-3">
          <span className="bg-black/40 backdrop-blur-md text-white text-xs font-bold px-2.5 py-1 rounded-full border border-white/10">
            #{i + 1}
          </span>
        </div>
      </a>
    ))}
  </div>
);

// ——— LinkedIn Post Card (square, text-first since posts aren't reels) ———
const LinkedInGrid = ({ posts }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
    {posts.map((p, i) => (
      <a
        key={i}
        href={p.url}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative block rounded-3xl overflow-hidden transition-transform duration-300 hover:scale-[1.02] min-h-[200px]"
      >
        <div className={`absolute inset-0 bg-gradient-to-br ${p.gradient}`} />
        <div className="relative p-6 h-full flex flex-col justify-between min-h-[200px]">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Linkedin size={16} className="text-white/80" />
              <span className="text-white/60 text-xs font-mono">#{i + 1}</span>
            </div>
            <p className="text-white text-sm font-semibold leading-snug line-clamp-4">{p.title}</p>
          </div>
          <div className="flex items-center gap-4 mt-4 text-white/80 text-xs">
            <span className="flex items-center gap-1"><Heart size={14} />{p.reactions}</span>
            <span className="flex items-center gap-1"><MessageCircle size={14} />{p.comments}</span>
            <span className="flex items-center gap-1"><Repeat2 size={14} />{p.reposts}</span>
          </div>
        </div>
      </a>
    ))}
  </div>
);

// ——— Top Reels Data (April 7 → May 9, 2026) ———
const igReels = [
  { title: 'This AI can build full courses now', metric: '6,746', gradient: 'from-purple-600 via-violet-500 to-fuchsia-400', url: 'https://www.instagram.com/p/DXUVGBzAdjk/', emoji: '🤖' },
  { title: 'This Boring Edtech model making 20 lakhs a month', metric: '1,965', gradient: 'from-emerald-500 via-teal-500 to-cyan-400', url: 'https://www.instagram.com/p/DXMS2RZkbnK/', emoji: '💰' },
  { title: 'The biggest pricing reset in performance marketing', metric: '1,516', gradient: 'from-amber-500 via-orange-500 to-red-500', url: 'https://www.instagram.com/p/DX9R9vIiCOj/', emoji: '📉' },
  { title: 'You hit ten lakhs a month and stayed stuck there', metric: '1,325', gradient: 'from-rose-600 via-red-500 to-orange-400', url: 'https://www.instagram.com/p/DX1yi1ANV6N/', emoji: '📈' },
  { title: '100 targeted sales calls a month → 10 Lakhs', metric: '492', gradient: 'from-blue-600 via-indigo-500 to-violet-500', url: 'https://www.instagram.com/p/DXJvvVqCenU/', emoji: '📞' },
];

const ytReels = [
  { title: 'This Boring EdTech model making 20 lakhs a month', metric: '1,437', gradient: 'from-red-600 via-rose-500 to-orange-400', url: 'https://www.youtube.com/shorts/si3JHfWONDQ', emoji: '💰' },
  { title: 'Stop running a Lala Edtech', metric: '1,369', gradient: 'from-red-700 via-red-500 to-pink-400', url: 'https://www.youtube.com/shorts/IZWwlOU_AgE', emoji: '🛑' },
  { title: 'META ADS ARE AN ILLUSION', metric: '1,226', gradient: 'from-blue-600 via-indigo-500 to-violet-500', url: 'https://www.youtube.com/shorts/ds0sgIFF_-0', emoji: '🎯' },
  { title: '60 seconds to build trust, not remorse', metric: '1,138', gradient: 'from-emerald-500 via-teal-500 to-cyan-400', url: 'https://www.youtube.com/shorts/xWFucCIWXF0', emoji: '⏱️' },
  { title: 'Never ask for budget', metric: '1,009', gradient: 'from-amber-500 via-orange-500 to-red-500', url: 'https://www.youtube.com/shorts/2QBYhoJUrOA', emoji: '💸' },
];

const fbReels = [
  { title: "People don't care about budget when handed free cash flow", metric: '10,000', gradient: 'from-blue-600 via-indigo-500 to-violet-500', url: 'https://www.facebook.com/reel/1079430148090338/', emoji: '💵' },
  { title: 'The biggest pricing reset in performance marketing', metric: '6,100', gradient: 'from-amber-500 via-orange-500 to-red-500', url: 'https://www.facebook.com/reel/1220820006795950/', emoji: '📉' },
  { title: '60 seconds to build trust, not remorse', metric: '362', gradient: 'from-emerald-500 via-teal-500 to-cyan-400', url: 'https://www.facebook.com/reel/2008028740067012/', emoji: '⏱️' },
  { title: '50,000 followers earning less than someone with 2,000', metric: '336', gradient: 'from-rose-600 via-red-500 to-orange-400', url: 'https://www.facebook.com/reel/1447867966457647/', emoji: '👥' },
  { title: 'Stop running a Lala Edtech', metric: '211', gradient: 'from-purple-600 via-violet-500 to-fuchsia-400', url: 'https://www.facebook.com/reel/886798187741082/', emoji: '🛑' },
];

const liPosts = [
  { title: 'Stop wasting hours tweaking audiences. Meta Advantage+ does it for you.', reactions: 6, comments: 0, reposts: 1, gradient: 'from-blue-700 via-blue-600 to-indigo-600', url: 'https://www.linkedin.com/posts/scale-ed_scaling-business-edtech-ugcPost-7449839303133937666-LPGr' },
  { title: 'Stop running a Lala Edtech', reactions: 6, comments: 0, reposts: 0, gradient: 'from-indigo-700 via-blue-600 to-cyan-500', url: 'https://www.linkedin.com/posts/scale-ed_scaling-business-edtech-activity-7449105820069416961-R1l6' },
  { title: 'AI is replacing roles. Not a prediction, already happening.', reactions: 5, comments: 1, reposts: 1, gradient: 'from-cyan-700 via-blue-700 to-indigo-700', url: 'https://www.linkedin.com/posts/scale-ed_ai-automation-business-ugcPost-7455265955493883904-w9Gy' },
  { title: 'The biggest pricing reset in performance marketing', reactions: 4, comments: 0, reposts: 1, gradient: 'from-blue-800 via-indigo-700 to-purple-700', url: 'https://www.linkedin.com/company/scale-ed/posts/' },
  { title: 'Never ask for budget', reactions: 4, comments: 0, reposts: 1, gradient: 'from-slate-700 via-blue-700 to-indigo-600', url: 'https://www.linkedin.com/company/scale-ed/posts/' },
  { title: '100 targeted calls a month → 10 lakhs', reactions: 3, comments: 0, reposts: 1, gradient: 'from-blue-600 via-indigo-600 to-violet-600', url: 'https://www.linkedin.com/company/scale-ed/posts/' },
];

// ——— Main Report ———
export default function SachinReportApril() {
  useEffect(() => {
    const pageTitle = 'Sachin – April 30-Day Report | SocialSEO';
    document.title = pageTitle;
    const ensureMeta = (selector, attributes) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        Object.entries(attributes).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      el.setAttribute('content', pageTitle);
    };
    ensureMeta('meta[property="og:title"]', { property: 'og:title' });
    ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title' });
  }, []);

  // Collective totals across captured platforms (excludes private impressions).
  const totalViews = 19278 + 15141 + 20452; // 54,871
  const totalEngagement = 176 + 239 + 15 + 90 + 94 + 42 + 2 + 13; // likes + reactions + comments + reposts

  return (
    <div className="font-sans leading-relaxed text-white relative" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');
      `}</style>

      <header className="fixed top-0 left-0 right-0 z-50 p-4 md:p-6 flex justify-between items-center bg-transparent pointer-events-none">
        <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
          <span className="font-bold tracking-tight text-purple-300">
            <span className="text-white">Social</span>SEO
          </span>
        </div>
        <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
          <span className="text-sm font-medium text-white/80">April 7 → May 9, 2026</span>
        </div>
      </header>

      <div className="relative z-10">
        <main>
          {/* Hero */}
          <section className="min-h-[50vh] flex flex-col justify-center px-4 pt-24 pb-8">
            <div className="max-w-6xl mx-auto w-full space-y-2">
              <p className="text-purple-400 font-bold text-sm uppercase tracking-widest">Social SEO</p>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white via-purple-100 to-white leading-tight">
                Sachin, April 30-Day Report
              </h1>
              <p className="text-lg text-white/60">Cross-platform performance: Instagram, YouTube, Facebook, LinkedIn</p>
            </div>
          </section>

          {/* Collective Totals */}
          <section className="pb-16 px-4">
            <div className="container mx-auto max-w-6xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="bg-purple-500/20 text-purple-300 p-2.5 rounded-xl"><Activity size={22} /></div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">30-Day Collective Performance</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <StatCard
                  icon={Eye}
                  iconColor="bg-pink-500/20 text-pink-300"
                  label="Total Public Views"
                  value={totalViews}
                  subtext="IG plays + YT views + FB views"
                />
                <StatCard
                  icon={Heart}
                  iconColor="bg-amber-500/20 text-amber-300"
                  label="Total Engagement"
                  value={totalEngagement}
                  subtext="Likes + reactions + comments + reposts"
                />
              </div>
            </div>
          </section>

          <div className="max-w-6xl mx-auto px-4"><div className="border-t border-white/10" /></div>

          {/* INSTAGRAM */}
          <section className="py-16 px-4">
            <div className="container mx-auto max-w-6xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="bg-purple-500/20 text-purple-400 p-2.5 rounded-xl"><Instagram size={22} /></div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">Instagram Performance</h2>
                <span className="text-white/40 text-sm font-mono">@thesachindahiya</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
                <StatCard icon={Play} iconColor="bg-purple-500/20 text-purple-300" label="Total Reels" value={30} subtext="Published in 30 days" />
                <StatCard icon={Eye} iconColor="bg-pink-500/20 text-pink-300" label="Total Plays" value={19278} subtext="Public play counter" />
                <StatCard icon={Heart} iconColor="bg-rose-500/20 text-rose-300" label="Likes" value={176} subtext="Aggregate across reels" />
                <StatCard icon={MessageCircle} iconColor="bg-amber-500/20 text-amber-300" label="Comments" value={94} subtext="Conversations sparked" />
              </div>
              <div className="mb-6 flex items-center gap-3">
                <h3 className="text-xl font-bold text-white">Top 5 Reels</h3>
                <span className="bg-purple-500/20 text-purple-300 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-purple-500/30">Instagram Reels</span>
              </div>
              <ReelGrid reels={igReels} metricLabel="Plays" />
            </div>
          </section>

          <div className="max-w-6xl mx-auto px-4"><div className="border-t border-white/10" /></div>

          {/* YOUTUBE */}
          <section className="py-16 px-4">
            <div className="container mx-auto max-w-6xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="bg-red-500/20 text-red-400 p-2.5 rounded-xl"><Youtube size={22} /></div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">YouTube Shorts Performance</h2>
                <span className="text-white/40 text-sm font-mono">@scalingedtechs</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
                <StatCard icon={Play} iconColor="bg-red-500/20 text-red-300" label="Total Shorts" value={30} subtext="Last 30 short-form posts" />
                <StatCard icon={Eye} iconColor="bg-pink-500/20 text-pink-300" label="Total Views" value={15141} subtext="Public counter" />
                <StatCard icon={Heart} iconColor="bg-rose-500/20 text-rose-300" label="Likes" value={239} subtext="Aggregate across shorts" />
                <StatCard icon={MessageCircle} iconColor="bg-amber-500/20 text-amber-300" label="Comments" value={42} subtext="Active discussion" />
              </div>
              <div className="mb-6 flex items-center gap-3">
                <h3 className="text-xl font-bold text-white">Top 5 Shorts</h3>
                <span className="bg-red-500/20 text-red-300 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-red-500/30">YouTube Shorts</span>
              </div>
              <ReelGrid reels={ytReels} metricLabel="Views" />
            </div>
          </section>

          <div className="max-w-6xl mx-auto px-4"><div className="border-t border-white/10" /></div>

          {/* FACEBOOK */}
          <section className="py-16 px-4">
            <div className="container mx-auto max-w-6xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="bg-blue-500/20 text-blue-400 p-2.5 rounded-xl"><Facebook size={22} /></div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">Facebook Reels Performance</h2>
                <span className="text-white/40 text-sm font-mono">/dahiyaconsulting</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
                <StatCard icon={Play} iconColor="bg-blue-500/20 text-blue-300" label="Total Reels" value={30} subtext="Published in 30 days" />
                <StatCard icon={Eye} iconColor="bg-pink-500/20 text-pink-300" label="Total Views" value={20452} subtext="Real reel-feed play counts" />
                <StatCard icon={Heart} iconColor="bg-rose-500/20 text-rose-300" label="Likes" value={15} subtext="Aggregate across reels" />
                <StatCard icon={Award} iconColor="bg-cyan-500/20 text-cyan-300" label="Top Reactions" value={12} subtext="Marked as top reactions" />
              </div>
              <div className="mb-6 flex items-center gap-3">
                <h3 className="text-xl font-bold text-white">Top 5 Reels</h3>
                <span className="bg-blue-500/20 text-blue-300 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-blue-500/30">Facebook Reels</span>
              </div>
              <ReelGrid reels={fbReels} metricLabel="Views" />
              <p className="text-white/40 text-xs mt-4 italic">View counts pulled from the Facebook Reels feed (matches Page Insights). Comment counts not exposed by the public reels feed.</p>
            </div>
          </section>

          <div className="max-w-6xl mx-auto px-4"><div className="border-t border-white/10" /></div>

          {/* LINKEDIN */}
          <section className="py-16 px-4">
            <div className="container mx-auto max-w-6xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="bg-sky-500/20 text-sky-400 p-2.5 rounded-xl"><Linkedin size={22} /></div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">LinkedIn Performance</h2>
                <span className="text-white/40 text-sm font-mono">company/scale-ed</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-10">
                <StatCard icon={Play} iconColor="bg-sky-500/20 text-sky-300" label="Posts Shipped" value={30} subtext="Last 30 days" />
                <StatCard icon={Users} iconColor="bg-blue-500/20 text-blue-300" label="Page Followers" value={252} subtext="Current count" />
                <StatCard icon={Heart} iconColor="bg-rose-500/20 text-rose-300" label="Reactions" value={90} subtext="Likes + celebrate + insight" />
                <StatCard icon={MessageCircle} iconColor="bg-amber-500/20 text-amber-300" label="Comments" value={2} subtext="Direct replies" />
                <StatCard icon={Repeat2} iconColor="bg-emerald-500/20 text-emerald-300" label="Reposts" value={13} subtext="Network amplification" />
              </div>
              <div className="mb-6 flex items-center gap-3">
                <h3 className="text-xl font-bold text-white">Top Posts</h3>
                <span className="bg-sky-500/20 text-sky-300 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-sky-500/30">LinkedIn</span>
              </div>
              <LinkedInGrid posts={liPosts} />
              <p className="text-white/40 text-xs mt-4 italic">Impression counts are private. Pull from LinkedIn Analytics export.</p>
            </div>
          </section>

          <div className="max-w-6xl mx-auto px-4"><div className="border-t border-white/10" /></div>

          {/* SUGGESTIONS */}
          <section className="py-16 px-4">
            <div className="container mx-auto max-w-6xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="bg-amber-500/20 text-amber-300 p-2.5 rounded-xl"><TrendingUp size={22} /></div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">What the data is telling us</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <GlassCard>
                  <p className="text-amber-300 text-xs font-bold uppercase tracking-widest mb-2">Hook 01</p>
                  <h3 className="text-white text-lg font-bold mb-2">Double down on the AI tool angle</h3>
                  <p className="text-white/70 text-sm leading-relaxed">"This AI can build full courses now" pulled 6,746 IG plays and 83 comments, the highest comment-to-play ratio of any reel this month. Audience is signalling strong appetite for hands-on AI tool walkthroughs. Recommend two to three more AI-tool reels per week.</p>
                </GlassCard>
                <GlassCard>
                  <p className="text-blue-300 text-xs font-bold uppercase tracking-widest mb-2">Distribution 02</p>
                  <h3 className="text-white text-lg font-bold mb-2">Facebook Reels are punching above weight</h3>
                  <p className="text-white/70 text-sm leading-relaxed">Two FB reels broke 6K and 10K plays this window, while the IG ceiling is 6.7K. Facebook's reel algorithm is currently favouring this niche. Cross-post every IG winner to FB Reels within 24 hours of release.</p>
                </GlassCard>
                <GlassCard>
                  <p className="text-rose-300 text-xs font-bold uppercase tracking-widest mb-2">Theme 03</p>
                  <h3 className="text-white text-lg font-bold mb-2">Pricing and economics own the top of feed</h3>
                  <p className="text-white/70 text-sm leading-relaxed">Four of the top five IG reels are pricing themed (pricing reset, ten lakhs ceiling, 100 calls a month, free cash flow). On FB the same pattern holds, with the pricing reset reel hitting 6.1K. Keep pricing and cash flow content at roughly half of weekly output.</p>
                </GlassCard>
                <GlassCard>
                  <p className="text-red-300 text-xs font-bold uppercase tracking-widest mb-2">Hook 04</p>
                  <h3 className="text-white text-lg font-bold mb-2">YouTube Shorts hit a ceiling, test new hooks</h3>
                  <p className="text-white/70 text-sm leading-relaxed">Top five Shorts are clustered at 1.0K to 1.4K views with no breakout. The current hook style appears capped. Recommend an A/B test on the first three seconds, contrast question, statistic, and contradiction openings to find the lever.</p>
                </GlassCard>
                <GlassCard className="md:col-span-2">
                  <p className="text-sky-300 text-xs font-bold uppercase tracking-widest mb-2">LinkedIn 05</p>
                  <h3 className="text-white text-lg font-bold mb-2">LinkedIn is leaving engagement on the table</h3>
                  <p className="text-white/70 text-sm leading-relaxed">252 page followers and 90 reactions across 30 posts works out to roughly 0.012 reactions per follower per post, well below a healthy B2B benchmark. Two parallel moves: sharpen the post tone for founders and operators, and grow the follower base via targeted DM outreach to the same audience that engages with Sachin's reels.</p>
                </GlassCard>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </div>
  );
}
